import { useState, Children } from "react";

/**
 * Tab component without react-router
 *
 * @param {React.FunctionComponent} props
 * @returns {JSX.Element}
 */
export function Tabs({ children, onTabChange }) {
	const [currentIndex, setCurrentIndex] = useState(0);

	if (children && children.length > 1) {
		Children.forEach(children, child => {
			if (child && child.type?.name !== Tab.name) {
				throw Error("Tabs must only have `Tab` children");
			}
		});
	} else {
		throw Error("Tab must have at least 2 Tab components.");
	}

	const setActiveTab = index => {
		setCurrentIndex(index);
		if (onTabChange) {
			onTabChange(index);
		}
		if (children[index]?.props?.onClick) {
			children[index].props.onClick(index);
		}
	};

	const dividedWidthForTabItem = 100 / children.length;

	const tabTitles = Children.map(children, (child, index) => {
		if (child) {
			return (
				<button
					style={{
						cursor: child?.props?.disabled ? "not-allowed" : "pointer",
						width: `${dividedWidthForTabItem}%`, // Dynamically calculate width here
					}}
					className={`konf-tab-title ${currentIndex === index ? "active" : ""}`}
					key={index}
					disabled={child?.props?.disabled}
					onClick={() => {
						if (!child?.props?.disabled) setActiveTab(index);
					}}
				>
					{child?.props?.title}
					{child?.props?.icon && <img src={child.props.icon} className="tab-icon ml-3" />}
				</button>
			);
		}
	});

	return (
		<>
			<div className="konf-tabs">
				{tabTitles}
				<span
					className="tab-bar"
					style={{
						width: `${dividedWidthForTabItem}%`,
						left: `${currentIndex * dividedWidthForTabItem}%`,
					}}
				></span>
			</div>
			<div className="tab-content">
				{Children.map(children, (child, index) => {
					if (currentIndex === index) return child.props.children;
				})}
			</div>
			<style>
				{`
				.konf-tabs{
					position:relative;
					display:block;
					padding:0;
					border-bottom:1px solid #e0e0e050;
				}
				.konf-tabs>button{
					position: relative;
					display:inline-block;
					text-decoration: none;
					padding: 15px;
					text-transform: uppercase;
					color: var(--fourthColor);
					background-color:white;
					text-align: center;
					vertical-align:middle;
					outline:none;
					border:none;
					transition:left .2s ease;
					white-space: nowrap; /* Prevent text wrapping */
				}
				.konf-tabs>button:not(.active):hover {
						color:var(--primaryColor);
				}
				.konf-tabs>button:not(.active):disabled {
						color:#d1d1d1;
				}
				.konf-tabs>button:not(.active):active {
						background-color:#c3c3c375;
				}
				button.active{
					color:var(--primaryColor);
				}
				.tab-bar{
						position: absolute;
						z-index: 10;
						bottom: 0;
						height: 2px;
						background: var(--primaryColor);
						display: block;
						transition: left .2s ease;
						-webkit-transition: left .2s ease;
				}
				.tab-content {
				    margin-top: 8px;
					background: var(--backgroundColor);
				}
				`}
			</style>
		</>
	);
}

export function Tab(props) {
	if (!props.title) {
		throw Error("Tab must have a title.");
	}
	return null; // Content is handled within Tabs component
}

Tab.defaultProps = {
	disabled: false
};